import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  BooleanField,
  DateField,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  AutocompleteInput,
  TextInput,
  NumberInput,
  BooleanInput,
  Create,
  Edit,
  SimpleForm,
  EditButton,
  FunctionField,
  useRecordContext,
  required,
  TopToolbar,
  FilterButton,
  CreateButton,
  useListContext,
  Link,
} from 'react-admin';
import { Tabs, Tab, Chip, Box } from '@mui/material';

// ==========================
// স্ট্যাটাস কালার ব্যাজ
// ==========================
const StatusBadge = () => {
  const record = useRecordContext();
  if (!record) return null;
  const colors = {
    Pending: '#f1c40f',
    Approved: '#2ecc71',
    Rejected: '#e74c3c',
  };
  return (
    <span style={{ color: colors[record.status] || '#ccc', fontWeight: 'bold' }}>
      {record.status}
    </span>
  );
};

// ==========================
// Duplicate Wallet ব্যাজ
// একই wallet number যদি অন্য কোনো একাউন্ট থেকেও উইথড্র করা হয়ে থাকে,
// তাহলে লাল "Duplicate Wallet" ব্যাজ দেখাবে, আর তার নিচে ওই অন্য একাউন্টগুলোর username
// ক্লিকযোগ্য লিংক হিসেবে দেখাবে (ক্লিক করলে ইউজারের প্রোফাইল খুলবে)।
// backend এর duplicate_wallet_users ফিল্ড থেকে ডেটা আসে।
// ==========================
const DuplicateWalletBadge = () => {
  const record = useRecordContext();
  const users = record?.duplicate_wallet_users || [];
  if (users.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
      <Chip
        label="Duplicate Wallet"
        color="error"
        size="small"
        sx={{ fontWeight: 'bold' }}
      />
      {users.map((u) => (
        <Link
          key={u.id}
          to={`/users/${u.id}/show`}
          onClick={(e) => e.stopPropagation()}
          style={{ color: '#2ecc71', textDecoration: 'underline', fontSize: 12 }}
        >
          {u.username}
        </Link>
      ))}
    </Box>
  );
};

// ==========================
// উপরে Tab দিয়ে All / Pending / Approved / Rejected ফিল্টার
// ==========================
const statusTabs = [
  { label: 'All', value: '' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Approved', value: 'Approved' },
  { label: 'Rejected', value: 'Rejected' },
];

const StatusFilterTabs = () => {
  const { filterValues, setFilters } = useListContext();
  const currentStatus = filterValues.status || '';

  const handleChange = (event, newValue) => {
    const newFilters = { ...filterValues };
    if (newValue === '') {
      delete newFilters.status;
    } else {
      newFilters.status = newValue;
    }
    setFilters(newFilters, {});
  };

  return (
    <Tabs
      value={currentStatus}
      onChange={handleChange}
      indicatorColor="primary"
      textColor="primary"
      sx={{ mb: 1 }}
    >
      {statusTabs.map((tab) => (
        <Tab key={tab.value} label={tab.label} value={tab.value} />
      ))}
    </Tabs>
  );
};

// ==========================
// List page এর উপরের action বাটনগুলো (Filter + Create)
// ==========================
const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <CreateButton />
  </TopToolbar>
);

const withdrawFilters = [
  <TextInput source="q" label="Search" alwaysOn />,
];

// ==========================
// List
// ==========================
export const WithdrawRequestList = (props) => (
  <List {...props} filters={withdrawFilters} actions={<ListActions />}>
    <>
      <StatusFilterTabs />
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="id" />
        <ReferenceField source="user" reference="users" label="Username">
          <TextField source="username" />
        </ReferenceField>
        <ReferenceField source="method" reference="withdraw-methods" label="Method">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="wallet_number" label="Wallet number" />
        <FunctionField label="Wallet Check" render={() => <DuplicateWalletBadge />} />
        <NumberField source="amount_bdt" label="Amount (BDT)" />
        <BooleanField source="is_crypto" label="Is crypto" />
        <NumberField source="amount_usd" label="Amount (USDT)" />
        <TextField source="order_id" label="Order" />
        <FunctionField label="Status" render={() => <StatusBadge />} />
        <DateField source="created_at" label="Created at" showTime />
        <EditButton />
      </Datagrid>
    </>
  </List>
);

// ==========================
// Create — এখানে তৈরি করলে ব্যাকএন্ড থেকে balance কেটে নেওয়া হবে
// ==========================
export const WithdrawRequestCreate = (props) => (
  <Create {...props}>
    <SimpleForm>
      <ReferenceInput source="user" reference="users" label="User">
        <AutocompleteInput optionText="username" validate={required()} />
      </ReferenceInput>

      <ReferenceInput source="method" reference="withdraw-methods" label="Method">
        <SelectInput optionText="name" validate={required()} />
      </ReferenceInput>

      <TextInput source="wallet_number" label="Wallet number" validate={required()} />
      <NumberInput source="amount_bdt" label="Amount (BDT)" validate={required()} />

      <BooleanInput source="is_crypto" label="Is crypto" defaultValue={false} />
      <NumberInput source="amount_usd" label="Amount (USDT)" />
    </SimpleForm>
  </Create>
);

// ==========================
// Edit — এখান থেকে Approve/Reject করলে ব্যাকএন্ড লজিক (রিফান্ড ইত্যাদি) চলবে
// ==========================
export const WithdrawRequestEdit = (props) => (
  <Edit {...props}>
    <SimpleForm>
      <ReferenceInput source="user" reference="users" label="User">
        <AutocompleteInput optionText="username" disabled />
      </ReferenceInput>

      <ReferenceInput source="method" reference="withdraw-methods" label="Method">
        <SelectInput optionText="name" />
      </ReferenceInput>

      <TextInput source="wallet_number" label="Wallet number" />
      <NumberInput source="amount_bdt" label="Amount (BDT)" />

      <BooleanInput source="is_crypto" label="Is crypto" />
      <NumberInput source="amount_usd" label="Amount (USDT)" />

      <SelectInput
        source="status"
        label="Status"
        choices={[
          { id: 'Pending', name: 'Pending' },
          { id: 'Approved', name: 'Approved' },
          { id: 'Rejected', name: 'Rejected' },
        ]}
        validate={required()}
      />
    </SimpleForm>
  </Edit>
);